import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { Store } from '@ngrx/store';
import {
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  Observable,
  startWith,
  Subscription,
  switchMap,
} from 'rxjs';
import { FormControl, Validators } from '@angular/forms';
import { AppState } from 'src/app/core/app.state';
import { LocalStorageService } from 'src/app/shared/localstorage.service';
import { parseOrdersFilterRequest, refillOrdersFilter } from 'src/app/shared/utils/helpers';
import { IOrderUser, IOrdersFilter } from '../../models/order.model';
import { FetchAllCatalogs } from 'src/app/features/catalogs/store/catalogs.actions';
import { ICatalog } from 'src/app/features/catalogs/models/catalogs.models';
import { OrdersService } from '../../services/orders.services';

@Component({
  selector: 'app-orders-filter',
  templateUrl: './orders-filter.component.html',
  styleUrls: ['./orders-filter.component.scss'],
})
export class OrdersFilterComponent implements OnInit, OnDestroy {
  readonly separatorKeysCodes = [ENTER, COMMA] as const;

  currentFilter: Observable<IOrdersFilter> = this.store.select(
    (state) => state.orders.ordersFilterState.advanced
  );
  ordersFilters = LocalStorageService.get('ordersFilters');

  selectedGroup = 'orders_info';

  filter: IOrdersFilter = refillOrdersFilter();
  filterName = '';
  toggleSaveFilterForm = false;
  filterNameControl = new FormControl('', Validators.required);

  catalogs$ = this.store.select((state) => state.catalogs.allCatalogs);
  filterCatalogs$ = this.catalogs$;
  catalogFilterKey: string = '';

  ownerSearchInput = new FormControl<string>('', { nonNullable: true });
  creatorSearchInput = new FormControl<string>('', { nonNullable: true });
  ownerOptions: IOrderUser[] = [];
  creatorOptions: IOrderUser[] = [];
  ownerSearchLoading = false;
  creatorSearchLoading = false;

  private subs: Subscription[] = [];

  constructor(
    public dialogRef: MatDialogRef<OrdersFilterComponent>,
    @Inject(MAT_DIALOG_DATA) public data: unknown,
    private store: Store<AppState>,
    private ordersService: OrdersService
  ) {}

  ngOnInit(): void {
    this.subs.push(
      this.currentFilter.subscribe((f: IOrdersFilter) => {
        this.filter = refillOrdersFilter(f);
        this.ensureOwnershipFilter();
        const own = this.filter.ownershipFilter;
        this.ownerSearchInput.setValue(own?.owner?.name ?? '', { emitEvent: false });
        this.creatorSearchInput.setValue(own?.creator?.name ?? '', { emitEvent: false });
      })
    );

    this.store.dispatch(FetchAllCatalogs());

    this.subs.push(
      this.ownerSearchInput.valueChanges
        .pipe(
          startWith(this.ownerSearchInput.value),
          debounceTime(300),
          distinctUntilChanged(),
          switchMap((text) => {
            const v = (text ?? '').trim();
            const sel = this.filter.ownershipFilter?.owner;
            if (sel && v !== sel.name) {
              this.filter.ownershipFilter!.owner = undefined;
            }
            this.ownerSearchLoading = true;
            return this.ordersService.searchBackofficeUsers(v, 50).pipe(
              finalize(() => {
                this.ownerSearchLoading = false;
              })
            );
          })
        )
        .subscribe((users) => {
          this.ownerOptions = users;
        })
    );

    this.subs.push(
      this.creatorSearchInput.valueChanges
        .pipe(
          startWith(this.creatorSearchInput.value),
          debounceTime(300),
          distinctUntilChanged(),
          switchMap((text) => {
            const v = (text ?? '').trim();
            const sel = this.filter.ownershipFilter?.creator;
            if (sel && v !== sel.name) {
              this.filter.ownershipFilter!.creator = undefined;
            }
            this.creatorSearchLoading = true;
            return this.ordersService.searchBackofficeUsers(v, 50).pipe(
              finalize(() => {
                this.creatorSearchLoading = false;
              })
            );
          })
        )
        .subscribe((users) => {
          this.creatorOptions = users;
        })
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  private ensureOwnershipFilter(): void {
    if (!this.filter.ownershipFilter) {
      this.filter.ownershipFilter = {};
    }
  }

  onOwnerSelected(event: MatAutocompleteSelectedEvent): void {
    const u = event.option.value as IOrderUser;
    this.ensureOwnershipFilter();
    this.filter.ownershipFilter!.owner = u;
    this.ownerSearchInput.setValue(u.name, { emitEvent: false });
    this.ownerOptions = [];
  }

  onCreatorSelected(event: MatAutocompleteSelectedEvent): void {
    const u = event.option.value as IOrderUser;
    this.ensureOwnershipFilter();
    this.filter.ownershipFilter!.creator = u;
    this.creatorSearchInput.setValue(u.name, { emitEvent: false });
    this.creatorOptions = [];
  }

  clearOwnerFilter(): void {
    this.ensureOwnershipFilter();
    this.filter.ownershipFilter!.owner = undefined;
    this.ownerSearchInput.setValue('', { emitEvent: true });
  }

  clearCreatorFilter(): void {
    this.ensureOwnershipFilter();
    this.filter.ownershipFilter!.creator = undefined;
    this.creatorSearchInput.setValue('', { emitEvent: true });
  }

  getFiltersCount(): number {
    return parseOrdersFilterRequest(this.filter)?.filters?.length || 0;
  }

  close(): void {
    this.dialogRef.close();
  }

  clearFilters(): void {
    this.filter = refillOrdersFilter();
    this.filterName = '';
    this.ownerSearchInput.setValue('', { emitEvent: false });
    this.creatorSearchInput.setValue('', { emitEvent: false });
  }

  selectCatalog(catalog: ICatalog): void {
    if (this.filter?.generalInfoFilter?.catalogs)
      this.filter?.generalInfoFilter?.catalogs?.push(catalog);
    this.catalogFilterKey = '';
    this.filterCatalogs();
  }

  getCatalogsNames(): string {
    return (this.filter?.generalInfoFilter?.catalogs || [])
      .map((catalog) => catalog.name)
      .join(', ');
  }

  filterCatalogs(): void {
    this.filterCatalogs$ = this.catalogs$.pipe(
      map((catalogs) =>
        catalogs.filter(
          (catalog) =>
            catalog.name
              .toLocaleLowerCase()
              ?.includes(this.catalogFilterKey.toLocaleLowerCase()!) &&
            !this.filter?.generalInfoFilter?.catalogs?.find((c) => c.id === catalog.id)
        )
      )
    );
  }

  toggleSaveFilter(): void {
    this.toggleSaveFilterForm = !this.toggleSaveFilterForm;
  }

  save(): boolean | void {
    if (!this.filterNameControl.valid) {
      this.filterNameControl.markAsTouched();
      return false;
    }
    if (!this.ordersFilters?.filters)
      this.ordersFilters = {
        filters: [{ name: this.filterName, filter: this.filter }],
      };
    else {
      const filter = this.ordersFilters.filters?.find(
        (f: { name: string }) => f.name.toLowerCase() === this.filterName.toLowerCase()
      );
      if (filter)
        this.ordersFilters.filters = this.ordersFilters.filters?.map((f: { name: string; filter: IOrdersFilter }) => {
          if (f.name === this.filterName) return { name: this.filterName, filter: this.filter };
          return f;
        });
      else
        this.ordersFilters.filters.push({
          name: this.filterName,
          filter: this.filter,
        });
    }
    LocalStorageService.put('ordersFilters', this.ordersFilters);
    this.toggleSaveFilter();
  }

  setFilter(filter: { name: string; filter: IOrdersFilter }): void {
    if (filter) {
      this.filterName = filter.name;
      this.filter = refillOrdersFilter(filter.filter);
      this.ensureOwnershipFilter();
      const own = this.filter.ownershipFilter;
      this.ownerSearchInput.setValue(own?.owner?.name ?? '', { emitEvent: false });
      this.creatorSearchInput.setValue(own?.creator?.name ?? '', { emitEvent: false });
    }
  }

  deleteFilter(filter: { name: string }): void {
    this.ordersFilters.filters = this.ordersFilters.filters?.filter(
      (f: { name: string }) => f.name !== filter.name
    );
    LocalStorageService.put('ordersFilters', this.ordersFilters);
  }

  apply(): void {
    this.dialogRef.close(this.filter);
  }
}
